import facilities from './facilities.json';

/**
 * Facility access helpers.
 *
 * Admins are normally scoped to an explicit `facilities` list stored in their
 * custom claims. Firebase caps custom claims at 1000 bytes (~50 facility ids),
 * so an admin cannot be granted every facility that way. Instead, the boolean
 * `allFacilities` claim grants an admin access to every *real* facility without
 * enumerating them. The two test facilities are intentionally excluded from
 * that grant.
 *
 * Precedence:
 *   superadmin            -> every facility (incl. test facilities)
 *   admin + allFacilities -> every facility except the test facilities
 *   admin / staff         -> only the ids listed in claims.facilities
 */

// Facilities that aren't real sites; never granted via the allFacilities claim.
export const TEST_FACILITY_IDS = ['test-facility', 'test-facility-two'];

export const isTestFacility = (facilityId) =>
  TEST_FACILITY_IDS.includes(facilityId);

// All real (non-test) facility ids.
export const realFacilityIds = facilities
  .filter((f) => !isTestFacility(f.id))
  .map((f) => f.id);

/** Does this user's claims grant access to a specific facility? */
export const canAccessFacility = (claims, facilityId) => {
  if (!claims || !facilityId) return false;
  if (claims.superadmin) return true;
  if (claims.admin && claims.allFacilities) return !isTestFacility(facilityId);
  return Array.isArray(claims.facilities)
    ? claims.facilities.includes(facilityId)
    : false;
};

/**
 * The facility ids a user may operate on — used to build switchers/pickers.
 * Superadmins keep access to everything (including test facilities); an
 * allFacilities admin gets every real facility.
 */
export const accessibleFacilityIds = (claims) => {
  if (!claims) return [];
  if (claims.superadmin) return facilities.map((f) => f.id);
  if (claims.admin && claims.allFacilities) return realFacilityIds;
  return Array.isArray(claims.facilities) ? claims.facilities : [];
};
